import { useEffect, useMemo } from 'react'
import Label from '../../ui/Label';
import { useDispatch, useSelector } from 'react-redux';
import { CreateClientRequest } from '../../../types/client';
import Loading from '../../ui/Loading';
import {  clearSelectedPackageToUpdate, selectClientData, selectCurrentClientId, selectTypeOfShowClient, selectUpdateClientPackageId, setClearTypeOfShowClient, setDefaultPackage, updateClientFormField } from '../../../features/client/clientSlice';
import ClientModalHeader from '../../layout/Admin/clients/ClientModalHeader';
import { selectCurrentUserRole } from '../../../features/auth/authSlice';
import { isAdmin, isDataAdmin, isSuperAdmin } from '../../../utils/roleUtils';
import ClientModalInputWrap from '../../layout/Admin/clients/ClientModalInputWrap';
import ClientModalDateInputWrap from '../../layout/Admin/clients/ClientModalDateInputWrap';
import ClientModalTimeInputWrap from '../../layout/Admin/clients/ClientModalTimeInputWrap';
import ClientModalChapelCheckbox from '../../layout/Admin/clients/ClientModalChapelCheckbox';
import ClientModalContactPerson from '../../layout/Admin/clients/ClientModalContactPerson';
import ClientModalBasicPackage from '../../layout/Admin/clients/ClientModalBasicPackage';
import ClientModalAllPackages from '../../layout/Admin/clients/ClientModalAllPackages';
import { useMasterData } from '../../../hooks/api/useMasterData';
import { useClientPopulateWrap } from '../../../hooks/populate/useClientPopulateWrap';
import ClientUpdateBtnWrap from '../../layout/Admin/clients/ClientUpdateBtnWrap';
import { useMutationData } from '../../../hooks/api/useMutationData';
import useClientAction from '../../../hooks/actions/useClientAction';
import ClientUpdatePackageProducts from '../Admin/ClientUpdatePackageProducts';
import ViewClientAvailPackageProduct from '../Admin/ViewClientAvailPackageProduct';
import { useDynamicQueryData } from '../../../hooks/api/useDynamicQueryData';

interface NewClientModalProps { 
    typeOfAction: "update" | "newClient";
}

export default function NewClientModal({ typeOfAction }: NewClientModalProps) {

    const dispatch = useDispatch();
    const clientData = useSelector(selectClientData);
    const clientId = useSelector(selectCurrentClientId);
    const adminRole = useSelector(selectCurrentUserRole);
    const clientTypeOfShow = useSelector(selectTypeOfShowClient);
    const openPackageId = useSelector(selectUpdateClientPackageId);
    const { handleCreateNewClient, isLoadingCreateClient, handleUpdateSchedule, cancelUpdate, isSchedule } = useClientAction()
    const hasAccessUpdateSchedule = isSuperAdmin(adminRole) || isAdmin(adminRole)
    const { getBasicPackage } = useMasterData()
    const { isLoadingClientInfo } = useClientPopulateWrap()
    const { isLoadingUpdateClientInfo } = useMutationData()
    const { packageSummaryBaseOnIdQuery, isErrorPackageSummaryBaseOnIdQuery,isLoadingPackageSummaryBaseOnIdQuery, errorMessagePackageSummaryBaseOnIdQuery } = useDynamicQueryData({clientId})

    useEffect(() => {
        if(typeOfAction !== 'newClient') return
        if(!getBasicPackage) return 
        dispatch(setDefaultPackage({
            id: getBasicPackage.packageID,
            name: getBasicPackage.packageName
        }))
    }, [typeOfAction, getBasicPackage, dispatch])

    useEffect(() => {
        if (typeOfAction !== 'newClient') return;

        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();

        const startMinutes = 10 * 60; // 10:00 AM
        const endMinutes = 20 * 60 + 59; // 08:59 PM

        let startTime: string;

        if (currentMinutes < startMinutes) {
            // Before 10:00 AM
            startTime = '10:00';
        } else if (currentMinutes > endMinutes) {
            // After 08:59 PM
            startTime = '20:59';
        } else {
            // Within allowed time
            startTime = now.toTimeString().slice(0, 5);
        }

        dispatch(updateClientFormField({
            field: 'startTime',
            value: startTime
        }));
    }, [typeOfAction, dispatch]);
    
    const handleChangeClient = (field: keyof CreateClientRequest, value: string) => {
        if(field === 'mobileNo') {

            if (!/^\d*$/.test(value)) return;
            if (!value.startsWith("09")) {
                value = "09";
            }
            if (value.length > 11) return;

            dispatch(updateClientFormField({
                field: field,
                value: value
            }));
            return
        }
        dispatch(updateClientFormField({
            field: field,
            value: value
        }));
    }

    const selectedPackage = useMemo(() => {
            return packageSummaryBaseOnIdQuery?.find(
                pkg => pkg.clientPackageId === openPackageId
            );
        }, [packageSummaryBaseOnIdQuery, openPackageId]);

    const onClose = () => {
        dispatch(clearSelectedPackageToUpdate())
        dispatch(setClearTypeOfShowClient())
    }

    return (
        <div className='fixed m-0 p-0 inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex justify-center items-center z-[100]'>
            <div className='relative w-[90vw] mx-5 bg-white rounded-lg shadow px-5 flex md:w-[75vw] lg:w-[70vw] xl:w-[70vw]'>
                <div className='flex flex-col w-full'>
                    <div className='py-5'>
                        <ClientModalHeader typeOfAction={typeOfAction} />
                        <hr className="h-px bg-gray-200 border-0" />
                        <div className='overflow-hidden rounded mt-2'>
                            <div className='px-2 h-full rounded overflow-y-auto max-h-[400px] lg:max-h-[500px]'>
                                {typeOfAction === 'update' && (isLoadingClientInfo)  ? (
                                    <Loading />
                                ) : (
                                    <div className='pt-2 space-y-2 overflow-x-hidden'>
                                        <div className='grid grid-cols-2 gap-1'>
                                            <Label label={'Client Information:'} customCSS={`text-sm font-medium`}/>
                                        </div>
                                        <div className='grid grid-cols-2 gap-3 '>
                                            <ClientModalInputWrap 
                                                label='Name of Deceased *' 
                                                type='text'
                                                value={clientData.deceasedName}
                                                handleChange={handleChangeClient}
                                                typeOfAction={typeOfAction}
                                                placeholder='Juan Dela Cruz'
                                                field='deceasedName'
                                            />
                                            <ClientModalInputWrap 
                                                label='Name of Client *' 
                                                type='text'
                                                value={clientData.registeredBy}
                                                handleChange={handleChangeClient}
                                                typeOfAction={typeOfAction}
                                                placeholder='Juan Dela Cruz'
                                                field='registeredBy'
                                            />
                                            <ClientModalDateInputWrap
                                                label='Start Date *' 
                                                type='date'
                                                value={clientData.scheduleFrom}
                                                handleChange={handleChangeClient}
                                                typeOfAction={typeOfAction}
                                                field='scheduleFrom'
                                                isUpdateSchedule={isSchedule}
                                            />
                                            <ClientModalDateInputWrap
                                                label='End Date *'
                                                type='date'
                                                value={clientData.scheduleTo}
                                                handleChange={handleChangeClient}
                                                typeOfAction={typeOfAction}
                                                field='scheduleTo'
                                                isUpdateSchedule={isSchedule}
                                            />
                                            <ClientModalTimeInputWrap
                                                label='Start Time *'
                                                type='time'
                                                value={clientData.startTime}
                                                handleChange={handleChangeClient}
                                                typeOfAction={typeOfAction}
                                                field='startTime'
                                                isUpdateSchedule={isSchedule}
                                            />
                                            <ClientModalTimeInputWrap
                                                label='End Time *'
                                                type='time'
                                                value={clientData.endTime}
                                                handleChange={handleChangeClient}
                                                typeOfAction={typeOfAction}
                                                field='endTime'
                                                isUpdateSchedule={isSchedule}
                                            />
                                            
                                            {(hasAccessUpdateSchedule && typeOfAction === 'update') && (
                                                <ClientUpdateBtnWrap
                                                    updateType="upSchedule"
                                                    isLoading={isLoadingUpdateClientInfo}
                                                    onSave={handleUpdateSchedule}
                                                    onCancel={cancelUpdate}
                                                />
                                            )}
                                            
                                            <ClientModalChapelCheckbox
                                                typeOfAction={typeOfAction}
                                            />
                                        </div>
                                        
                                        <div className='py-2 space-y-2'>
                                            <div className='grid grid-cols-2 gap-2'>
                                                <Label label={'Heritage Information:'} customCSS={`text-sm font-medium`}/>
                                            </div>
                                            <div className='grid grid-cols-1 md:grid-cols-2 gap-1 md:gap-2 lg:gap-3'>
                                                <ClientModalInputWrap 
                                                    label='Heritage Mobile No. *' 
                                                    type='number'
                                                    value={clientData.mobileNo}
                                                    handleChange={handleChangeClient}
                                                    typeOfAction={typeOfAction}
                                                    placeholder='09123456789'
                                                    field='mobileNo'
                                                    required={true}
                                                    maxLength={11}
                                                />
                                                <ClientModalInputWrap 
                                                    label='Heritage Email Address *' 
                                                    type='email'
                                                    value={clientData.email}
                                                    handleChange={handleChangeClient}
                                                    typeOfAction={typeOfAction}
                                                    placeholder='example@gmail.com'
                                                    field='email'
                                                    required={true}
                                                />
                                            </div>
                                        </div>
                                        <ClientModalContactPerson typeOfAction={typeOfAction}/>
                                        {typeOfAction === 'newClient' && ( <ClientModalBasicPackage/> )}
                                        <ClientModalAllPackages typeOfAction={typeOfAction} />
                                        {typeOfAction === 'newClient' && (
                                            <button 
                                                onClick={handleCreateNewClient}
                                                disabled={isLoadingCreateClient}
                                                className={`w-full text-[13px] px-4 py-2 rounded font-medium transition-all bg-cbColor text-white ${isLoadingCreateClient && 'bg-gray-200  cursor-not-allowed'}`}
                                            >
                                                {
                                                    isLoadingCreateClient ? (
                                                        <Loading 
                                                            setH={'h-[20px]'} 
                                                            message={'Saving...'}
                                                        />
                                                    ) : 'Save new client'
                                                }
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {clientTypeOfShow === 'showUpdateProducts' &&  <ClientUpdatePackageProducts/>}
            {clientTypeOfShow === 'showPackageProduct' &&  
                <ViewClientAvailPackageProduct 
                    isLoading={isLoadingPackageSummaryBaseOnIdQuery}
                    isError={isErrorPackageSummaryBaseOnIdQuery}
                    errormessage={errorMessagePackageSummaryBaseOnIdQuery}
                    product={selectedPackage?.products ?? []}
                    onClose={onClose}
                />
            }
        </div>
    )
}
